import type { ReactNode } from 'react';
import { TAGLINE } from '../constants';
import { useBranding } from '../lib/useBranding';

interface AuthShellProps {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * Support-searchable diagnostic ref (e.g. "FRED-42") rendered subtly
   * under the card on error states — same convention as the FRED-7 RAG
   * canary, so a support ticket quoting it greps straight to the server
   * log line.
   */
  diagnosticRef?: string | null;
}

export default function AuthShell({
  title,
  subtitle,
  children,
  footer,
  diagnosticRef,
}: AuthShellProps) {
  const { copyright_holder } = useBranding();
  return (
    <main className="fred-auth-backdrop min-h-full flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-card">
          <div className="text-center mb-8">
            <div
              className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-md bg-brand-orange text-white text-2xl font-extrabold shadow-lg shadow-brand-orange/25"
              aria-hidden="true"
            >
              F
            </div>
            {/* Dual-tone brand headline (the product name split for the
                accent): structural words in cream, the emphasis keyword
                in the orange accent (BRAND.md). Reads as one phrase. */}
            <h1 className="text-3xl font-extrabold tracking-[-0.04em] text-brand-cream">
              Talk to <span className="text-brand-orange">Fred</span>
            </h1>
            <p className="mt-2 text-sm text-dark-muted">{TAGLINE}</p>
          </div>
          <section aria-labelledby="auth-card-title" className="fred-auth-card">
            <header className="mb-5">
              <h2
                id="auth-card-title"
                className="text-xl font-semibold tracking-tight text-brand-cream"
              >
                {title}
              </h2>
              {subtitle ? <p className="mt-1.5 text-sm text-dark-secondary">{subtitle}</p> : null}
            </header>
            {children}
          </section>
          {diagnosticRef ? (
            <p className="mt-3 text-center text-xs text-dark-muted/80" data-testid="diagnostic-ref">
              Ref {diagnosticRef}
            </p>
          ) : null}
          {footer ? <div className="mt-4 text-center text-sm">{footer}</div> : null}
        </div>
      </div>
      <footer className="py-5 text-center text-xs text-dark-muted">
        &copy; {new Date().getFullYear()} {copyright_holder}
      </footer>
    </main>
  );
}
